import { DateTime } from 'luxon'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faClone, faCog, faPencil, faTrashAlt,
} from '@fortawesome/pro-light-svg-icons'

import { digObject } from '@domain-group/javascript-utils'
import {
  Box, Button, IconWithMenu, ListItem, Tag, Text,
} from '@domain-group/suit-theme'

import handleCallbackAction from '@functions/handleCallbackAction'
import { buildItemTitle, buildOptionsString } from '@functions/orderItem'
import { confirmDelete } from '@functions/entity'

import useOrderItem from '@hooks/useOrderItem'
import useRedirectUrlWithCacheKey from '@hooks/useRedirectUrlWithCacheKey'

import OrderItemOrQuoteItemManagement from '@components/OrderItemOrQuoteItemManagement'

import defaultRequestOptions from '@sections/Client/packs/Campaign/defaultRequestOptions'

import type { OrderItemModel } from '@models/orderItem'

interface OrderItemProps {
  orderItem: OrderItemModel,
}

const OrderItem = (props: OrderItemProps) => {
  const { orderItem } = props

  const orderItemPayload = useOrderItem(orderItem)
  const {
    callbacks: {
      deleteOrderItem: deleteFn,
      duplicateOrderItem: duplicateFn,
      manageOrderItem,
    },
    isDeletable,
    isDuplicatable,
    orderItem: {
      options: orderItemOptions,
    },
  } = orderItemPayload

  const dueDateString = digObject(orderItem, 'dates.due.date')
  const orderItemDueDate = dueDateString ? DateTime.fromFormat(dueDateString, 'dd/MM/yyyy') : null

  const showAdditionalOptions = isDeletable || isDuplicatable

  const { redirectUrl } = useRedirectUrlWithCacheKey([orderItem.id])

  return (
    <ListItem boxProps={{ borderBottom: '1px dashed', padding: 'large' }} disableHover>
      <Box flexDirection="column">
        <Box flexDirection="row" alignItems="center">
          <Box flexDirection="column">
            <Text fontSize="small">
              {buildItemTitle({ orderableItem: orderItem, excludeOptionsString: true })}
            </Text>

            <Text color="bodyFontLightColor" fontSize="xsmall" marginTop="medium">
              {/*
               // @ts-expect-error TS2345 */}
              {buildOptionsString(orderItemOptions)}
            </Text>

            {orderItemDueDate && (
              <Tag boxProps={{ marginRight: 'auto', marginTop: 'medium' }}>
                Due {orderItemDueDate.toFormat('d LLL yyyy')}
              </Tag>
            )}
          </Box>

          <Box marginLeft="auto" width="auto">
            <Button
              buttonStyle="secondaryUtility"
              onClick={() => manageOrderItem()}
              size="medium"
              // @ts-expect-error TS2322
              width="auto"
            >
              <FontAwesomeIcon icon={faPencil} />
            </Button>

            {showAdditionalOptions && (
              <IconWithMenu
                icon={(
                  // @ts-expect-error TS2322
                  <Button buttonStyle="secondaryUtility" marginLeft="small" size="medium" width="auto">
                    <FontAwesomeIcon icon={faCog} />
                  </Button>
                )}
              >
                {/*
                 // @ts-expect-error TS2741 */}
                <IconWithMenu.Menu closeOnClick listStyle={{ top: 'calc(100% + 12px)', right: '-7px' }}>
                  {isDuplicatable && (
                    <a
                      key="duplicate"
                      onClick={() => handleCallbackAction({
                        callbacks: {
                          action: duplicateFn,
                        },
                        entityParams: orderItem,
                        requestOptions: {
                          ...defaultRequestOptions.orderItem,
                          ...defaultRequestOptions.artwork,
                          ...defaultRequestOptions.artworkPage,
                        },
                        toastText: 'Order Item Duplicated',
                      })}
                    >
                      <FontAwesomeIcon icon={faClone} /> Duplicate Item
                    </a>
                  )}

                  {isDeletable && (
                    <a
                      key="delete"
                      onClick={() => confirmDelete({
                        callbacks: {
                          deleteFn,
                        },
                        strings: {
                          confirmString: 'Are you sure? This is not reversable.',
                          entityName: 'Order Item',
                        },
                      })}
                    >
                      <FontAwesomeIcon icon={faTrashAlt} /> Delete Item
                    </a>
                  )}
                </IconWithMenu.Menu>
              </IconWithMenu>
            )}
          </Box>
        </Box>

        <OrderItemOrQuoteItemManagement
          boxProps={{ marginTop: 'medium' }}
          enabledFeatureKeys={['artwork.create', 'artwork.edit', 'orderItemTask.manage']}
          itemPayload={orderItemPayload}
          redirectUrl={redirectUrl}
        />
      </Box>
    </ListItem>
  )
}

export default OrderItem
